import { useLocalStorage } from "src/utils/useLocalStorage"
import { StatusOption } from "./DataSourcesFilter"

interface UseStatusFilterOptions {
  /** The key to use for localStorage persistence */
  storageKey?: string
  /** Custom status options (defaults to standard data source statuses) */
  statusOptions?: StatusOption[]
  /** Values to exclude from the default filter */
  excludeByDefault?: string[]
}

interface UseStatusFilterReturn {
  /** The current status filter */
  statusFilter: StatusOption[]
  /** Function to update the status filter */
  setStatusFilter: (value: StatusOption[]) => void
  /** Available status options */
  statusOptions: StatusOption[]
  /** Helper function to get status label from status value */
  getStatus: (status: string) => string
}

const DEFAULT_STATUS_OPTIONS: StatusOption[] = [
  { label: "Added", value: ["INGESTED"] },
  { label: "In Progress", value: ["UPLOADED", "UPLOADING", "OCR"] },
  { label: "Error", value: ["ERROR"] },
  { label: "Deleted", value: ["REMOVED"] },
  { label: "Pending", value: ["PENDING"] }
]

/**
 * Custom hook that manages status filter state with localStorage persistence
 *
 * @param options Configuration options for the hook
 * @returns Object containing statusFilter, setStatusFilter, statusOptions, and getStatus
 *
 * @example
 * // Basic usage with defaults
 * const { statusFilter, setStatusFilter, statusOptions, getStatus } = useStatusFilter({
 *   storageKey: "myStatusFilter"
 * })
 *
 * @example
 * // With custom status options
 * const customOptions = [
 *   { label: "Active", value: ["ACTIVE"] },
 *   { label: "Inactive", value: ["INACTIVE"] }
 * ]
 * const { statusFilter, setStatusFilter } = useStatusFilter({
 *   storageKey: "customStatusFilter",
 *   statusOptions: customOptions,
 *   excludeByDefault: ["INACTIVE"]
 * })
 */
export const useStatusFilter = (options: UseStatusFilterOptions = {}): UseStatusFilterReturn => {
  const {
    storageKey = "statusFilter",
    statusOptions = DEFAULT_STATUS_OPTIONS,
    excludeByDefault = ["REMOVED"]
  } = options

  // Calculate default filter (exclude specified values)
  const defaultFilter = statusOptions.filter((option) => !option.value.some((val) => excludeByDefault.includes(val)))

  const [statusFilter, setStatusFilter] = useLocalStorage<StatusOption[]>(storageKey, defaultFilter)

  /**
   * Helper function to get the status label from a status value
   * @param status - The status value to look up
   * @returns The corresponding label or the original status if not found
   */
  const getStatus = (status: string): string => {
    return statusOptions.find((option) => option.value.includes(status))?.label || status
  }

  return {
    statusFilter,
    setStatusFilter,
    statusOptions,
    getStatus
  }
}
